import { I18N_NAMESPACES } from "@src/appV2/i18n/constants";
import { useTu } from "@src/appV2/i18n/useTu";
import { useMemo } from "react";

export const DEFAULT_NAVIGATION_TRANSLATION_LABELS = {
  accounts: "Accounts",
  adminTools: "Admin Tools",
  analytics: "Analytics",
  applicationAndJobs: "Application & Jobs",
  availability: "Availability",
  badgeStrikeReviews: "Badge-Strike Reviews",
  badgeVerificationGrading: "Badge Verification Grading",
  bans: "Bans",
  billing: "Billing",
  browseProfessionals: "Browse Professionals",
  browseTalentPool: "Browse Talent Pool",
  byNumber: "By Number",
  byPeriod: "By Period",
  calendar: "Calendar",
  calendarClassic: "Calendar (Classic)",
  candidateProfile: "Candidate Profile",
  cases: "Cases",
  changeLog: "Change Log",
  charting: "Charting",
  chat: "Chat",
  clipboardScore: "Clipboard Score",
  connectEmr: "Connect EMR",
  contractTerms: "Contract Terms",
  copied: "Copied!",
  corporateAnalytics: "Corporate Analytics",
  corporateCenter: "Corporate Center",
  coverageMap: "Coverage Map",
  dnr: "DNR",
  dnrs: "DNRs",
  documents: "Documents",
  eligibility: "Eligibility",
  employees: "Employees",
  exclusions: "Exclusions",
  externalPhotos: "External Photos",
  gettingStarted: "Getting started",
  helpCenter: "Help Center",
  hiring: "Hiring",
  homeHealthWorkerSearch: "Home Health Worker Search",
  identityVerification: "Identity Verification",
  interviewAvailability: "Interview Availability",
  interviewSchedule: "Interview Schedule",
  interviews: "Interviews",
  invoice: "Invoice",
  invoiceHistory: "Invoice History",
  landingPage: "Landing Page",
  liveWorkerEta: "Live Worker ETA",
  locations: "Locations",
  logOut: "Log Out",
  logOutFromAllDevices: "Log Out From All Devices",
  management: "Management",
  myAccount: "My Account",
  myInternalRoster: "My Internal Roster",
  myProfessionals: "My Professionals",
  newBadge: "New",
  noShows: "No Shows",
  payroll: "Payroll",
  permanentHiring: "Permanent Hiring",
  placement: "Placement",
  postInvoiceDisputes: "Post-Invoice Disputes",
  preInvoiceDisputes: "Pre-Invoice Disputes",
  professionalId: "Professional ID",
  professionalName: "Professional Name",
  professionals: "Professionals",
  qualifications: "Qualifications",
  recentlyViewed: "Recently Viewed",
  referAndEarn: "Refer & Earn",
  reporting: "Reporting",
  requirements: "Requirements",
  reviewsModeration: "Reviews Moderation",
  schedule: "Schedule",
  scraping: "Scraping",
  settings: "Settings",
  shiftExplainer: "Shift Explainer",
  shiftLogs: "Shift Logs",
  shiftPayments: "Shift Payments",
  shiftSearch: "Shift Search",
  shifts: "Shifts",
  shiftsVerification: "Shifts Verification",
  smsPortal: "SMS Portal",
  spendControls: "Spend Controls",
  ssnWorkerLookup: "SSN Worker Lookup",
  staffing: "Staffing",
  stripePayouts: "Stripe Payouts",
  stripeTransfers: "Stripe Transfers",
  tapToCopy: "Tap to copy",
  teamMembers: "Team Members",
  timeclockRequests: "Timeclock Requests",
  timekeepingVideos: "Timekeeping Videos",
  upcomingCharges: "Upcoming Charges",
  visitVerification: "Visit Verification",
  workerEvidences: "Worker Evidences",
  workers: "Workers",
  workplaceAnalytics: "Workplace Analytics",
  workplaceId: "Workplace ID",
  workplaceInformation: "Workplace Information",
  workplaceName: "Workplace Name",
  workplaceRequirements: "Workplace Requirements",
  workplaceRulesQuiz: "Workplace Rules Quiz",
  rulesAndExpectations: "Rules & Expectations",
  workplaces: "Workplaces",
  workplaceTypes: "Workplace Types",
};

export type NavigationTranslationLabels = {
  [Key in keyof typeof DEFAULT_NAVIGATION_TRANSLATION_LABELS]: string;
};

export function useNavigationTranslationLabels(): NavigationTranslationLabels {
  const { tu } = useTu(I18N_NAMESPACES.navigation);

  return useMemo(
    () => ({
      accounts: tu("Accounts"),
      adminTools: tu("Admin Tools"),
      analytics: tu("Analytics"),
      applicationAndJobs: tu("Application & Jobs"),
      availability: tu("Availability"),
      badgeStrikeReviews: tu("Badge-Strike Reviews"),
      badgeVerificationGrading: tu("Badge Verification Grading"),
      bans: tu("Bans"),
      billing: tu("Billing"),
      browseProfessionals: tu("Browse Professionals"),
      browseTalentPool: tu("Browse Talent Pool"),
      byNumber: tu("By Number"),
      byPeriod: tu("By Period"),
      calendar: tu("Calendar"),
      calendarClassic: tu("Calendar (Classic)"),
      candidateProfile: tu("Candidate Profile"),
      cases: tu("Cases"),
      changeLog: tu("Change Log"),
      charting: tu("Charting"),
      chat: tu("Chat"),
      clipboardScore: tu("Clipboard Score"),
      connectEmr: tu("Connect EMR"),
      contractTerms: tu("Contract Terms"),
      copied: tu("Copied!"),
      corporateAnalytics: tu("Corporate Analytics"),
      corporateCenter: tu("Corporate Center"),
      coverageMap: tu("Coverage Map"),
      dnr: tu("DNR"),
      dnrs: tu("DNRs"),
      documents: tu("Documents"),
      eligibility: tu("Eligibility"),
      employees: tu("Employees"),
      exclusions: tu("Exclusions"),
      externalPhotos: tu("External Photos"),
      gettingStarted: tu("Getting started"),
      helpCenter: tu("Help Center"),
      hiring: tu("Hiring"),
      homeHealthWorkerSearch: tu("Home Health Worker Search"),
      identityVerification: tu("Identity Verification"),
      interviewAvailability: tu("Interview Availability"),
      interviewSchedule: tu("Interview Schedule"),
      interviews: tu("Interviews"),
      invoice: tu("Invoice"),
      invoiceHistory: tu("Invoice History"),
      landingPage: tu("Landing Page"),
      liveWorkerEta: tu("Live Worker ETA"),
      locations: tu("Locations"),
      logOut: tu("Log Out"),
      logOutFromAllDevices: tu("Log Out From All Devices"),
      management: tu("Management"),
      myAccount: tu("My Account"),
      myInternalRoster: tu("My Internal Roster"),
      myProfessionals: tu("My Professionals"),
      newBadge: tu("New"),
      noShows: tu("No Shows"),
      payroll: tu("Payroll"),
      permanentHiring: tu("Permanent Hiring"),
      placement: tu("Placement"),
      postInvoiceDisputes: tu("Post-Invoice Disputes"),
      preInvoiceDisputes: tu("Pre-Invoice Disputes"),
      professionalId: tu("Professional ID"),
      professionalName: tu("Professional Name"),
      professionals: tu("Professionals"),
      qualifications: tu("Qualifications"),
      recentlyViewed: tu("Recently Viewed"),
      referAndEarn: tu("Refer & Earn"),
      reporting: tu("Reporting"),
      requirements: tu("Requirements"),
      reviewsModeration: tu("Reviews Moderation"),
      schedule: tu("Schedule"),
      scraping: tu("Scraping"),
      settings: tu("Settings"),
      shiftExplainer: tu("Shift Explainer"),
      shiftLogs: tu("Shift Logs"),
      shiftPayments: tu("Shift Payments"),
      shiftSearch: tu("Shift Search"),
      shifts: tu("Shifts"),
      shiftsVerification: tu("Shifts Verification"),
      smsPortal: tu("SMS Portal"),
      spendControls: tu("Spend Controls"),
      ssnWorkerLookup: tu("SSN Worker Lookup"),
      staffing: tu("Staffing"),
      stripePayouts: tu("Stripe Payouts"),
      stripeTransfers: tu("Stripe Transfers"),
      tapToCopy: tu("Tap to copy"),
      teamMembers: tu("Team Members"),
      timeclockRequests: tu("Timeclock Requests"),
      timekeepingVideos: tu("Timekeeping Videos"),
      upcomingCharges: tu("Upcoming Charges"),
      visitVerification: tu("Visit Verification"),
      workerEvidences: tu("Worker Evidences"),
      workers: tu("Workers"),
      workplaceAnalytics: tu("Workplace Analytics"),
      workplaceId: tu("Workplace ID"),
      workplaceInformation: tu("Workplace Information"),
      workplaceName: tu("Workplace Name"),
      workplaceRequirements: tu("Workplace Requirements"),
      workplaceRulesQuiz: tu("Workplace Rules Quiz"),
      rulesAndExpectations: tu("Rules & Expectations"),
      workplaces: tu("Workplaces"),
      workplaceTypes: tu("Workplace Types"),
    }),
    [tu]
  );
}
